import { useCallback, useEffect, useState } from "react";

import { useSnackbar } from "notistack";

import { RecaptchaAction } from "../../../common/constants/recaptcha";
import { getTelemetryID } from "../../../common/helpers/stytch";
import { trpc } from "../../../common/helpers/trpc";
import { useRecaptchaRetry } from "../../../common/hooks/useRecaptchaRetry";

import { useTranslate } from "@/common/hooks/useTranslate";

const DEFAULT_RESEND_COOLDOWN = 120;

type UseResendCodeProps =
  | {
      email?: never;
    }
  | {
      feature: "login";
      email: string;
    };

export const useResendCode = ({ email }: UseResendCodeProps) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { executeTrpc } = useRecaptchaRetry();

  const { mutateAsync: reSendSmsOtp, isPending: isLoadingSendSms } = trpc.reSendSmsOtp.useMutation();

  const [resendCooldown, setResendCooldown] = useState(0);

  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const resendButtonText = t("dashboard", "settings.twoFactorAuthBySmsModal.setup.code.resend");

  const isResendButtonDisabled = resendCooldown > 0 || isLoadingSendSms;
  const resendButtonCountdownText = resendCooldown > 0 ? `${resendButtonText} - ${resendCooldown}s` : resendButtonText;

  const triggerCooldown = useCallback(() => {
    setResendCooldown(DEFAULT_RESEND_COOLDOWN);
    const timer = setInterval(() => {
      setResendCooldown((prevCooldown) => (prevCooldown <= 0 ? 0 : prevCooldown - 1));
    }, 1000);
    setTimer(timer);
  }, []);

  const resendCode = useCallback(async () => {
    const response = await executeTrpc(RecaptchaAction.SendSmsOtp, email || "", reSendSmsOtp, {
      telemetryId: (await getTelemetryID()) || undefined,
    });
    if (response.error) {
      enqueueSnackbar(t("dashboard", "common.gqlError"), { vibe: "danger" });
    } else {
      triggerCooldown();
      enqueueSnackbar(t("dashboard", "settings.twoFactorAuthBySmsModal.setup.code.resend.sent"), { vibe: "positive" });
    }
  }, [email, enqueueSnackbar, executeTrpc, reSendSmsOtp, t, triggerCooldown]);

  useEffect(() => {
    if (resendCooldown <= 0 && timer) {
      clearTimeout(timer);
    }
  }, [resendCooldown, timer]);

  return {
    isResendButtonDisabled,
    resendButtonCountdownText,
    isLoadingSendSms,
    resendCode,
  };
};
